import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getPretestingPrograms } from "../../utils/api/endpoints/programs/pretesting";
import PhaseStatusPill, { PhaseStatus } from "./PhaseStatusPill";
import MarkReadyButton from "./MarkReadyButton";
import { cropSentence } from "../../utils/formatText";
import ComputerIcon from "../../assets/icons/ComputerIcon";

interface PretestingProgram {
  program_id: number;
  program_title: string;
  description?: string;
  type?: string;
  phase_status: PhaseStatus;
  expected_start_date?: string;
  expected_end_date?: string;
  createdAt: string;
}

const PretestingProgramsList: React.FC = () => {
  const [programs, setPrograms] = useState<PretestingProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  // Add CSS animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      const response = await getPretestingPrograms();
      setPrograms(response.programs || []);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch programs";
      
      // Handle specific error cases gracefully
      if (errorMessage.includes('403')) {
        // Researcher not authorized - show empty state
        setPrograms([]);
        setError(null);
      } else if (errorMessage.includes('Program is not activated') || 
                 errorMessage.includes('not activated')) {
        // This is not an error - program is just inactive (which is normal for pretesting)
        setPrograms([]);
        setError(null);
      } else if (errorMessage.includes('No programs found') || 
                 errorMessage.includes('no programs')) {
        // No programs available - show empty state
        setPrograms([]);
        setError(null);
      } else {
        // Real error - show error message
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  useEffect(() => {
    if (activeIdx >= programs.length) setActiveIdx(0);
  }, [programs, activeIdx]);

  const goPrev = () => {
    if (!programs.length) return;
    setActiveIdx((idx) => (idx - 1 + programs.length) % programs.length);
  };

  const goNext = () => {
    if (!programs.length) return;
    setActiveIdx((idx) => (idx + 1) % programs.length);
  };

  const handleMarkReadySuccess = () => {
    // Refresh the programs list after successful marking
    fetchPrograms();
  };

  const handleMarkReadyError = (errorMessage: string) => {
    // Keep list visible; optionally show inline error if needed
    setError(errorMessage);
  };

  const formatProgramType = (raw?: string): string => {
    if (!raw) return "Not specified";
    const val = String(raw).trim();
    if (/^ptaas$/i.test(val)) return "PTaaS";
    return val;
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return "TBD";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
      });
    } catch {
      return "TBD";
    }
  };

  return (
    <div>
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-blue-400 rounded-full animate-ping"></div>
          </div>
          <p className="mt-4 text-sm font-medium text-gray-600">Loading pretesting programs...</p>
        </div>
      ) : error ? (
        <div className="mx-4 my-6">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-semibold text-red-800">Unable to load programs</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
                <button 
                  onClick={fetchPrograms}
                  className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : programs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 px-6">
          <h3 className="text-base font-semibold text-gray-900 mb-1 text-center">No programs currently visible</h3>
          <p className="text-xs text-gray-600 text-center max-w-sm leading-relaxed">
            There are no programs currently visible in your pretesting phase. This could be because:
          </p>
          
          {/* Information about program visibility */}
          <div className="mt-4 space-y-2">
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Programs will appear 1 day before their start date</span>
            </div>
            
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Only programs assigned to you will be visible</span>
            </div>
            
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Programs in pretesting phase are not yet activated</span>
            </div>
            
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <span>Check back later or contact your admin for assignments</span>
            </div>
          </div>
          
          {/* Refresh button for convenience */}
          <button 
            onClick={fetchPrograms}
            className="mt-4 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          >
            <svg className="w-3.5 h-3.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Programs
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {(programs.length > 0 ? [programs[activeIdx]] : []).map((program, index) => (
            <div 
              key={program.program_id} 
              className="bg-white rounded-xl border border-gray-200 shadow-sm transition-all duration-300 overflow-hidden"
              style={{
                animationDelay: `${index * 100}ms`,
                animation: 'fadeInUp 0.6s ease-out forwards'
              }}
            >
              {/* Header Row (no inner container, no underline, no blue icon) */}
              <div className="px-6 py-5">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <Link
                      to={`/dashboard/programs/${program.program_id}`}
                      className="block truncate text-2xl font-semibold text-[#0B1739]"
                      title={program.program_title}
                    >
                      {cropSentence(program.program_title, 60)}
                    </Link>
                    <div className="mt-2 flex items-center gap-3">
                      <PhaseStatusPill status={program.phase_status} />
                    </div>
                  </div>
                  {/* Right action */}
                  <div className="flex-shrink-0">
                    <MarkReadyButton
                      programId={program.program_id}
                      currentPhase={program.phase_status}
                      onSuccess={handleMarkReadySuccess}
                      onError={handleMarkReadyError}
                      className="px-6 py-2.5 text-sm font-medium"
                    />
                  </div>
                </div>
              </div>

              {/* Content Section */}
              <div className="px-6 py-4">
                {/* Description intentionally omitted to match requested UI */}
                
                {/* Program Details Grid */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded-xl bg-gray-50 p-4 ring-1 ring-gray-100">
                    <div className="mb-2 flex items-center gap-2">
                      <div className="h-2.5 w-2.5 rounded-full bg-blue-500"></div>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-600">Program Type</span>
                    </div>
                    <p className="text-base font-semibold text-[#0B1739]">{formatProgramType(program.type)}</p>
                  </div>
                  
                  <div className="rounded-xl bg-gray-50 p-4 ring-1 ring-gray-100">
                    <div className="mb-2 flex items-center gap-2">
                      <div className="h-2.5 w-2.5 rounded-full bg-green-500"></div>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-600">Expected Start</span>
                    </div>
                    <p className="text-base font-semibold text-[#0B1739]">{formatDate(program.expected_start_date)}</p>
                    {program.expected_start_date && (
                      <div className="mt-2">
                        {(() => {
                          const startDate = new Date(program.expected_start_date);
                          const today = new Date();
                          const diffTime = startDate.getTime() - today.getTime();
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          
                          if (diffDays === 0) {
                            return (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                Starting today
                              </span>
                            );
                          } else if (diffDays === 1) {
                            return (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                Starting tomorrow
                              </span>
                            );
                          } else if (diffDays > 1) {
                            return (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                In {diffDays} days
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Quick Actions */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Ready to start testing when activated</span>
                    </div>
                    
                <Link
                  to={`/dashboard/programs/${program.program_id}`}
                  className="inline-flex items-center text-xs font-medium text-blue-600 transition-colors duration-200"
                    >
                      View Details
                      <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {programs.length > 1 && (
            <div className="mt-4 flex items-center justify-center gap-4">
              <button
                aria-label="Previous program"
                onClick={goPrev}
                className="rounded-full border border-gray-200 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
              >
                Prev
              </button>
              <div className="flex items-center justify-center gap-2">
                {programs.map((_, i) => (
                  <button
                    key={i}
                    aria-label={`Go to program ${i + 1}`}
                    onClick={() => setActiveIdx(i)}
                    className={`h-2.5 rounded-full transition-all ${
                      i === activeIdx ? 'w-6 bg-[#0B1739]' : 'w-2.5 bg-gray-300'
                    }`}
                  />
                ))}
              </div>
              <button
                aria-label="Next program"
                onClick={goNext}
                className="rounded-full border border-gray-200 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
     </div>
  );
};

export default PretestingProgramsList;
